import classNames from 'classnames';
import { isEmpty, omit, trim, uniq, difference, keys } from 'lodash';
import { InputHTMLAttributes, memo, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { MdClose, MdChevronLeft } from 'react-icons/md';
import { QuestionProps } from '../../pa-prop-types';
import { QsOptions } from '../../patient-types';
import { propsToIgnore } from './qs-const';
import { useStaticLocales } from '../../../admin/hooks/locales/use-static-locale';
import { useTranslations } from '../../hooks/use-translations';
import { useSurveyState } from '../../hooks/use-survey-state';
import { useAutocomplete } from './hooks/useAutocomplete';
import Modal from '../../../components/Modal';
import { getMutualExclusiveAnswerKeys } from '../../../utils/models/question';
import produce from 'immer';
import QsTextInput from './QsTextInput';
import { resolveNestedQuestionLabel } from './nested-question-label';

interface QsInputMultiTextNestedProps extends InputHTMLAttributes<HTMLInputElement>, QuestionProps {
  isDisplayMode?: boolean;
  options?: QsOptions | null;
  onUpdate: (value?: string, answerMaps?: any[]) => void;
  focusOnLoad?: boolean;
  restrictInput?: 'valid name' | null;
  nestedQuestions?: Array<{
    question: string;
    options: Array<{
      key: string;
      label: string;
      requireNote?: boolean;
      mutuallyExclusive?: boolean;
    }>;
  }>;
  answerMaps?: Array<{
    answerKeys: string[];
    answerLabels: string[];
    notesForKeys?: Record<string, string>;
  }>;
}

function QsInputMultiTextNested(props: QsInputMultiTextNestedProps) {
  const { 
    focusOnLoad, 
    isDisplayMode, 
    onUpdate, 
    className, 
    value, 
    options, 
    error, 
    restrictInput, 
    question,
    locale,
    nestedQuestions = [],
    answerMaps: answerMapsProp
  } = props;
  const { max, min } = options || {};
  const filteredProps = omit(props, [...propsToIgnore, 'restrictInput']);

  const { sltStr } = useStaticLocales();
  const [, { t }] = useTranslations({ locale });

  const getNestedQuestionLabel = useCallback(
    (questionText?: string | null) =>
      resolveNestedQuestionLabel({
        questionText,
        t: (key) => t(key),
        sltStrByKey: (key) =>
          sltStr({
            key: key as Parameters<typeof sltStr>[0]['key'],
            upper: true,
          }),
      }),
    [sltStr, t]
  );
  const inputRef = useRef<HTMLInputElement>(null);

  // surveyState 사용
  const [{ surveyState }, { setSurveyState }] = useSurveyState();

  // 질문 타입 체크
  const isNestedAllergyQuestion = useMemo(() => {
    return question?.questionKey === 'q-pmhx-allergy-cause-nested-athena' || question?.questionKey === 'q-pmhx-allergy-cause-nested';
  }, [question?.questionKey]);

  // nestedQuestions를 answerOptionsMap으로 변환
  const answerOptionsMap = useMemo(() => {
    const map: Record<string, any> = {};
    
    nestedQuestions?.forEach((nestedQuestion, questionIndex) => {
      nestedQuestion.options.forEach((option, optionIndex) => {
        const optionKey = `nested-${questionIndex}-${optionIndex}`;
        
        map[optionKey] = {
          label: option.label || option.key,
          requireNote: option.requireNote || false,
          mutuallyExclusive: option.mutuallyExclusive || false,
        };
      });
    });
    
    return map;
  }, [nestedQuestions]);

  // 입력값 상태 관리
  const [inputValue, setInputValue] = useState('');
  const [tags, setTags] = useState<Array<{
    id: string;
    text: string;
    selectedOptions: string[];
    notesForKeys?: Record<string, string>;
  }>>([]);

  // allergy 자동완성을 위한 간단한 로직
  const [hasProcessedAllergy, setHasProcessedAllergy] = useState(false);
  
  // 사용자가 직접 태그를 추가했는지 추적
  const [hasUserAddedTag, setHasUserAddedTag] = useState(false);

  // 최신 값을 참조하기 위한 ref
  const inputValueRef = useRef(inputValue);
  const tagsRef = useRef(tags);

  // ref 업데이트
  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  useEffect(() => {
    tagsRef.current = tags;
  }, [tags]);
  
  // 모달 상태 관리
  const [openSelect, setOpenSelect] = useState(false);
  const [selectedTagIndex, setSelectedTagIndex] = useState<number | null>(null);
  const [markError, setMarkError] = useState(false);

  // mutually exclusive 로직을 위한 가상 question 객체 생성
  const virtualQuestion = useMemo(() => {
    if (!nestedQuestions || nestedQuestions.length === 0) return null;
    
    const allOptions = nestedQuestions.flatMap((nestedQuestion, questionIndex) => 
      nestedQuestion.options.map((option, optionIndex) => ({
        key: `nested-${questionIndex}-${optionIndex}`,
        label: option.label,
        requireNote: option.requireNote || false,
        mutuallyExclusive: option.mutuallyExclusive || false,
      }))
    );

    return {
      answerKeys: allOptions.map(opt => opt.key),
      answerOptionsMap,
    };
  }, [nestedQuestions, answerOptionsMap]);

  const mutExcAnswerKeys = getMutualExclusiveAnswerKeys({ question: virtualQuestion || undefined });

  // 안정적인 함수 참조를 위한 ref
  const setSurveyStateRef = useRef(setSurveyState);
  const onUpdateRef = useRef(onUpdate);
  
  // ref 업데이트
  useEffect(() => {
    setSurveyStateRef.current = setSurveyState;
    onUpdateRef.current = onUpdate;
  }, [setSurveyState, onUpdate]);

  // 자동완성 커스텀 훅 사용 (Nested Allergy는 자동완성 없이 단순 입력만)
  const {
    autoFillValue,
  } = useAutocomplete({
    isAllergyQuestion: false,
    isMedicationQuestion: false,
    isPharmacyQuestion: false,
    isPCPQuestion: false,
    isNestedAllergyQuestion,
    surveyState,
    setSurveyStateRef,
    onUpdateRef,
  });

  // 자동완성 데이터가 들어오면 자동으로 값 채우기 (최초 1회만)
  // inputValue 의존성을 제거하여 사용자 입력 중에 자동완성이 간섭하지 않도록 함
  useEffect(() => {
    autoFillValue(inputValue, setInputValue);
  }, []);

  // value prop과 answerMaps prop이 변경되면 tags와 inputValue 업데이트
  useEffect(() => {
    if (value !== undefined) {
      const valueStr = String(value || '');
      
      // 현재 태그와 비교해서 다를 때만 업데이트
      const currentTagsStr = tags.map(tag => tag.text).join('|');
      
      // 사용자가 이미 태그를 추가한 경우, 자동완성 값으로 덮어쓰지 않음
      // 단, 새로운 태그가 기존 태그보다 많은 경우에만 업데이트 (사용자가 다른 곳에서 추가한 경우)
      if (valueStr !== currentTagsStr) {
        // 사용자가 직접 태그를 추가한 경우, 덮어쓰지 않음
        if (hasUserAddedTag && tags.length > 0) {
          // 새로운 값이 기존 태그들을 포함하는 경우에만 업데이트
          const currentTagTexts = tags.map(tag => tag.text);
          let newTagTexts: string[] = [];
          if (valueStr.includes('|')) {
            newTagTexts = valueStr.split('|').filter(tag => trim(tag) !== '');
          } else if (valueStr) {
            newTagTexts = [valueStr];
          }
          
          // 새 태그가 기존 태그들을 모두 포함하고 추가 태그가 있는 경우에만 업데이트
          const hasNewTags = newTagTexts.some(text => !currentTagTexts.includes(text));
          const allCurrentTagsIncluded = currentTagTexts.every(text => newTagTexts.includes(text));
          
          if (!hasNewTags || !allCurrentTagsIncluded) {
            return; // 덮어쓰지 않음
          }
        }
        
        if (valueStr.includes('|')) {
          const tagTexts = valueStr.split('|').filter(tag => trim(tag) !== '');
          
          // answerMaps prop이 있으면 기존 정보를 복원
          const newTags = tagTexts.map((text, index) => {
            // answerMaps에서 해당 인덱스의 정보 찾기
            const answerMap = answerMapsProp && answerMapsProp[index];
            
            // answerKeys를 selectedOptions로 변환
            let selectedOptions: string[] = [];
            if (answerMap?.answerKeys) {
              selectedOptions = answerMap.answerKeys;
            }
            
            // notesForKeys 복원
            const notesForKeys: Record<string, string> = {};
            if (answerMap?.notesForKeys) {
              Object.assign(notesForKeys, answerMap.notesForKeys);
            }
            
            return {
              id: `tag-${Date.now()}-${index}`,
            text,
              selectedOptions,
              notesForKeys
            };
          });
          
          setTags(newTags);
          setInputValue('');
        } else if (valueStr === '') {
          // 빈 값일 때만 태그 초기화 (사용자가 직접 초기화한 경우만)
          if (!hasUserAddedTag || tags.length === 0) {
          setTags([]);
          setInputValue('');
          }
        } else if (tags.length === 0) {
          // 단일 값인 경우 (태그가 없을 때만 설정)
          setTags([]);
          setInputValue(valueStr);
        }
      }
    }
  }, [value, answerMapsProp, hasUserAddedTag, tags]); // hasUserAddedTag와 tags 의존성 추가

  // allergyFinal 상태 업데이트 함수 (useEffect보다 먼저 정의)
  const updateAllergyFinalState = useCallback((currentTags: typeof tags) => {
    const allergyFinal = currentTags.map(tag => {
      // 가장 높은 depth의 데이터 (알러지 이름)
      const allergyName = tag.text;
      
      // 그 다음 depth의 데이터들 (반응들) - 시스템 키를 사용
      const reactions = tag.selectedOptions.map(optionKey => {
        const parts = optionKey.split('-');
        const questionIndex = parseInt(parts[1], 10);
        const optionIndex = parseInt(parts[2], 10);
        const nestedQuestion = nestedQuestions[questionIndex];
        const option = nestedQuestion?.options[optionIndex];
        // label 대신 key를 사용해야 함
        return option?.key || optionKey;
      });

      return {
        allergy: allergyName,
        reactions
      };
    });

    setSurveyStateRef.current((prev: any) => {
      // 이전 allergyFinal과 비교해서 다를 때만 업데이트
      const prevAllergyFinal = prev?.allergyFinal || [];
      const prevAllergyFinalStr = JSON.stringify(prevAllergyFinal);
      const newAllergyFinalStr = JSON.stringify(allergyFinal);
      
      if (prevAllergyFinalStr !== newAllergyFinalStr) {
        return {
          ...prev,
          allergyFinal,
        };
      }
      return prev;
    });
  }, [nestedQuestions]);

  // tags 상태가 변경될 때 allergyFinal 상태 업데이트
  useEffect(() => {
    if (isNestedAllergyQuestion && tags.length > 0) {
      updateAllergyFinalState(tags);
    }
  }, [tags, isNestedAllergyQuestion, updateAllergyFinalState]); // tags, isNestedAllergyQuestion, updateAllergyFinalState 의존성

  // surveyState.allergyFinal이 변경될 때 태그 추가 (초기 로드 시에만)
  useEffect(() => {
    if (
      isNestedAllergyQuestion && 
      surveyState?.allergyFinal && 
      surveyState.allergyFinal.length > 0 &&
      !hasProcessedAllergy &&
      tags.length === 0 &&
      !hasUserAddedTag
    ) {

      const reactionMappingObject = {
        "angioedema": "sx-angioedema",
        "hives": "sx-urticaria",
        "anaphylaxis": "sx-anaphylaxis",
        "abdominal pain": "sx-abd-pain",
        "diarrhea": "sx-diarrhea",
        "irregular heart rate": "sx-irregular-heartbeat",
        "palpitations": "sx-palpitation",
        "confusion": "sx-confusion",
        "dizziness": "sx-dizziness",
        "seizure": "sx-seizure",
        "eye redness": "sx-ih-eye-red",
        "stevens-johnson syndrome": "o-stevens-johnson",
        "arthralgia": "o-joint-pain",
        "other": "o-other",
      }
      
      // allergyFinal을 태그로 변환
      const allergyTags = surveyState.allergyFinal.map((allergy, index) => {
        // reactions를 nestedQuestions의 옵션과 매핑
        const selectedOptions: string[] = [];
        
        if (allergy.reactions && nestedQuestions.length > 0) {
          allergy.reactions.forEach((reaction: any) => {
            const reactionName = reaction.reactionname || reaction;
            
            // reactionMappingObject에서 매핑된 키 찾기
            const mappedKey = reactionMappingObject[reactionName.toLowerCase() as keyof typeof reactionMappingObject];
            
            if (mappedKey) {
              // 첫 번째 nested question의 옵션들 중에서 매핑된 키와 일치하는 것 찾기
              const matchingOptionIndex = nestedQuestions[0].options.findIndex(
                option => option.key === mappedKey
              );
              
              if (matchingOptionIndex !== -1) {
                selectedOptions.push(`nested-0-${matchingOptionIndex}`);
              }
            }
          });
        }
        
        return {
          id: `allergy-${index}`,
          text: allergy.allergenname || '',
          selectedOptions,
          notesForKeys: {}
        };
      }).filter(tag => tag.text.trim() !== '');
      
      if (allergyTags.length > 0) {
        setTags(allergyTags);
        setInputValue('');
        
        // allergy 자동완성 완료 후 onUpdate 호출 (setTimeout으로 지연)
        const combinedValue = allergyTags.map(tag => tag.text).join('|');
        
      // answerMaps 생성
      const answerMaps = allergyTags.map(tag => ({
        answerKeys: tag.selectedOptions || [],
        answerLabels: tag.selectedOptions.map(optKey => {
          const optParts = optKey.split('-');
          const optQuestionIndex = parseInt(optParts[1], 10);
          const optOptionIndex = parseInt(optParts[2], 10);
          const optNestedQuestion = nestedQuestions[optQuestionIndex];
          const optOption = optNestedQuestion?.options[optOptionIndex];
          return t(optOption?.label || optKey);
        }) || [],
        notesForKeys: tag.notesForKeys || {}
      }));
        
        setTimeout(() => {
          onUpdate(combinedValue, answerMaps);
        }, 0);
        setHasProcessedAllergy(true);
        // athena에서 자동완성으로 불러온 경우, 사용자 추가 플래그는 설정하지 않음
      }
    }
  }, [surveyState?.allergyFinal, isNestedAllergyQuestion, hasProcessedAllergy, hasUserAddedTag, tags.length, onUpdate, nestedQuestions, t]);

  // 포커스 온 로드
  useEffect(() => {
    if (focusOnLoad && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focusOnLoad]);

  // 태그 추가 함수
  const addTag = useCallback((tagValue: string) => {
    const trimmedValue = trim(tagValue);
    if (trimmedValue && !tags.some(tag => tag.text === trimmedValue)) {
      const newTag = {
        id: `tag-${Date.now()}`,
        text: trimmedValue,
        selectedOptions: [],
        notesForKeys: {}
      };
      const newTags = [...tags, newTag];
      setTags(newTags);
      setInputValue('');
      
      // 사용자가 직접 태그를 추가했다고 표시
      setHasUserAddedTag(true);
      
      // 부모 컴포넌트에 업데이트된 값 전달 (setTimeout으로 지연)
      const combinedValue = newTags.map(tag => tag.text).join('|');
      
      // answerMaps 생성
      const answerMaps = newTags.map(tag => ({
        answerKeys: tag.selectedOptions || [],
        answerLabels: tag.selectedOptions.map(optKey => {
          const optParts = optKey.split('-');
          const optQuestionIndex = parseInt(optParts[1], 10);
          const optOptionIndex = parseInt(optParts[2], 10);
          const optNestedQuestion = nestedQuestions[optQuestionIndex];
          const optOption = optNestedQuestion?.options[optOptionIndex];
          return t(optOption?.label || optKey);
        }) || [],
        notesForKeys: tag.notesForKeys || {}
      }));
      
      setTimeout(() => {
        onUpdate(combinedValue, answerMaps);
      }, 0);

      // surveyState에 allergyFinal 업데이트
      if (isNestedAllergyQuestion) {
        updateAllergyFinalState(newTags);
      }
    }
  }, [tags, onUpdate, isNestedAllergyQuestion, nestedQuestions, t]);

  // 태그 제거 함수
  const removeTag = useCallback((indexToRemove: number) => {
    const newTags = tags.filter((_, index) => index !== indexToRemove);
    setTags(newTags);
    
    // 부모 컴포넌트에 업데이트된 값 전달 (setTimeout으로 지연)
    const combinedValue = newTags.length > 0 ? newTags.map(tag => tag.text).join('|') : '';
    
    // answerMaps 생성
    const answerMaps = newTags.map(tag => ({
      answerKeys: tag.selectedOptions || [],
      answerLabels: tag.selectedOptions.map(optKey => {
        const optParts = optKey.split('-');
        const optQuestionIndex = parseInt(optParts[1], 10);
        const optOptionIndex = parseInt(optParts[2], 10);
        const optNestedQuestion = nestedQuestions[optQuestionIndex];
        const optOption = optNestedQuestion?.options[optOptionIndex];
        return t(optOption?.label || optKey);
      }) || [],
      notesForKeys: tag.notesForKeys || {}
    }));
    
    setTimeout(() => {
      onUpdate(combinedValue, answerMaps);
    }, 0);

    // surveyState에 allergyFinal 업데이트
    if (isNestedAllergyQuestion) {
      updateAllergyFinalState(newTags);
    }
  }, [tags, onUpdate, isNestedAllergyQuestion]);

  // 중첩 옵션 선택 모달 열기
  const openNestedOptions = useCallback((tagIndex: number) => {
    if (isDisplayMode) return;
    setSelectedTagIndex(tagIndex);
    setOpenSelect(true);
    setMarkError(false);
  }, [isDisplayMode]);

  // 중첩 옵션 선택 모달 닫기
  const closeNestedOptions = useCallback(() => {
    setOpenSelect(false);
    setSelectedTagIndex(null);
  }, []);

  // 중첩 옵션 선택 핸들러 (QsInputSelectSheet와 동일한 로직)
  const handleNestedOptionChange = useCallback((optionKey: string, checked: boolean) => {
    if (selectedTagIndex === null) return;
    
    const newTags = [...tags];
    const currentTag = newTags[selectedTagIndex];
    const selectedKeys = currentTag.selectedOptions;
    
    const isMutExcl = mutExcAnswerKeys.includes(optionKey);
    const extNoteRequired = !!answerOptionsMap?.[optionKey]?.requireNote;
    
    let nKeys: string[] = [];
    
    if (checked) {
      if (isMutExcl) {
        nKeys = uniq([optionKey]);
      } else {
        nKeys = uniq([...selectedKeys, optionKey]);
        nKeys = nKeys?.filter((k) => {
          return !mutExcAnswerKeys.includes(k);
        });
      }
    } else {
      nKeys = selectedKeys.filter((nv) => nv !== optionKey);
    }
    
    currentTag.selectedOptions = nKeys;
    setTags(newTags);

    // 부모 컴포넌트에 업데이트된 값 전달
    const combinedValue = newTags.map(tag => tag.text).join('|');
    const answerMaps = newTags.map(tag => ({
      answerKeys: tag.selectedOptions || [],
      answerLabels: tag.selectedOptions.map(optKey => {
        const optParts = optKey.split('-');
        const optQuestionIndex = parseInt(optParts[1], 10);
        const optOptionIndex = parseInt(optParts[2], 10);
        const optNestedQuestion = nestedQuestions[optQuestionIndex];
        const optOption = optNestedQuestion?.options[optOptionIndex];
        return t(optOption?.label || optKey);
      }) || [],
      notesForKeys: tag.notesForKeys || {}
    }));
    
    setTimeout(() => {
      onUpdate(combinedValue, answerMaps);
    }, 0);

    // surveyState에 allergyFinal 업데이트
    if (isNestedAllergyQuestion) {
      updateAllergyFinalState(newTags);
    }
  }, [selectedTagIndex, tags, isNestedAllergyQuestion, mutExcAnswerKeys, answerOptionsMap, onUpdate]);


  // 입력값 변경 핸들러
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  // 키보드 핸들러 (단순화)
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Enter 키로 추가하는 기능 제거 (이제 + 버튼만 사용)
  }, []);



  // 유효성 검사
  let valid = true;
  let errorMessage = null;

  if (valid && min != null) {
    if (tags.length < Number(min)) {
      valid = false;
      errorMessage = `Please enter at least ${min} item(s).`;
    }
  }
  if (valid && max != null) {
    if (tags.length > Number(max)) {
      valid = false;
      errorMessage = `You can enter up to ${max} item(s) only.`;
    }
  }

  const hasValue = tags.length > 0 || !isEmpty(trim(inputValue));
  const currentTag = selectedTagIndex !== null ? tags[selectedTagIndex] : null;

  return (
    <div className="QsInputMultiTextNested">
      {errorMessage && <div className='text-error opacity-70 p-1'>{errorMessage}</div>}
      
      <div className="relative">
        {/* 입력 필드와 ADD 버튼이 포함된 컨테이너 */}
        <div className="relative">
          <input
            ref={inputRef}
            id={question?.questionKey}
            data-question-key={question?.questionKey}
            className={classNames(
              'input input-bordered w-full rounded-xl pr-20',
              className,
              hasValue && !isDisplayMode && !valid && 'input-error',
              error && 'input-error'
            )}
            {...filteredProps}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={sltStr({ key: 'label-describe-in-detail', upper: true })}
          />
          {/* ADD 버튼 */}
          <button
            type="button"
            onClick={() => {
              if (trim(inputValue)) {
                addTag(inputValue);
              }
            }}
            disabled={!trim(inputValue)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 btn btn-sm add-btn"
            style={{ 
              height: '32px', 
              minHeight: '32px',
              borderRadius: '8px',
              backgroundColor: trim(inputValue) ? '#3555FC' : '#E5E7EB',
              borderColor: trim(inputValue) ? '#3555FC' : '#E5E7EB',
              cursor: trim(inputValue) ? 'pointer' : 'not-allowed',
              paddingLeft: '12px',
              paddingRight: '12px'
            }}
          >
            <span style={{ 
              color: trim(inputValue) ? "white" : "#9CA3AF",
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {sltStr({ key: 'btn-add', upper: true })}
            </span>
          </button>
          
        </div>
        
        
        {/* 태그들 - 이미지와 동일한 형태 */}
        {tags.length > 0 && (
          <div className="mt-3 space-y-3">
            {tags.map((tag, index) => (
              <div 
                key={tag.id} 
                className="border rounded-lg p-4 cursor-pointer hover:bg-blue-50 transition-colors relative" 
                style={{ borderColor: '#3555FC', backgroundColor: 'transparent' }}
                onClick={() => openNestedOptions(index)}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-lg" style={{ color: '#3555FC' }}>{tag.text}</span>
                  {!isDisplayMode && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTag(index);
                      }}
                      className="btn btn-ghost btn-xs btn-circle bg-white hover:bg-gray-100"
                    >
                      <MdClose size={16} />
                    </button>
                  )}
                </div>
                
                {/* 질문 텍스트부터 메뉴 아이템까지의 회색 세로선 */}
                <div className="flex gap-3">
                  <div className="w-1 bg-gray-300 rounded-full" style={{ height: tag.selectedOptions.length > 0 ? '140px' : '80px' }}></div>
                  <div className="flex-1">
                    {/* 질문 텍스트와 SELECT 드롭다운 */}
                    <div className="mb-3">
                      <div className="text-gray-700 mb-3">
                        {nestedQuestions.length > 0
                          ? getNestedQuestionLabel(nestedQuestions[0].question)
                          : sltStr({ key: 'label-describe-in-detail', upper: true })}
                      </div>
                      <div className="relative">
                        <div className="w-full p-3 border border-gray-300 rounded-lg bg-white cursor-pointer hover:border-gray-400 transition-colors">
                          <span className="text-gray-500">{sltStr({ key: 'label-select', upper: true })}</span>
                          <MdChevronLeft 
                            size={16} 
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 -rotate-90 text-gray-400" 
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* 선택된 옵션들 - 태그 형태로 표시 */}
                    {tag.selectedOptions.length > 0 && (
                      <div className="bg-white rounded-lg">
                        <div className="flex flex-wrap gap-2">
                          {tag.selectedOptions.map(optionKey => {
                            // optionKey 형식: "nested-{questionIndex}-{optionIndex}"
                            const parts = optionKey.split('-');
                            const questionIndex = parseInt(parts[1], 10);
                            const optionIndex = parseInt(parts[2], 10);
                            const nestedQuestion = nestedQuestions[questionIndex];
                            const option = nestedQuestion?.options[optionIndex];
                            
                            return (
                              <div
                                key={optionKey}
                                className="inline-flex items-center gap-1 pl-3 pr-1 py-2 rounded-lg"
                                style={{ 
                                  height: '40px',
                                  minHeight: '40px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  backgroundColor: '#E0E5FF',
                                  color: '#3455FC'
                                }}
                              >
                                <span>{t(option?.label || optionKey)}</span>
                                {!isDisplayMode && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // 태그 표시 부분에서는 해당 태그의 인덱스를 직접 사용
                                      const newTags = [...tags];
                                      const targetTag = newTags[index];
                                      targetTag.selectedOptions = targetTag.selectedOptions.filter(key => key !== optionKey);
                                      setTags(newTags);
                                      
                                      // 부모 컴포넌트에 업데이트된 값 전달
                                      const combinedValue = newTags.map(tagItem => tagItem.text).join('|');
                                      const answerMaps = newTags.map(tagItem => ({
                                        answerKeys: tagItem.selectedOptions || [],
                                        answerLabels: tagItem.selectedOptions.map(optKey => {
                                          const optParts = optKey.split('-');
                                          const optQuestionIndex = parseInt(optParts[1], 10);
                                          const optOptionIndex = parseInt(optParts[2], 10);
                                          const optNestedQuestion = nestedQuestions[optQuestionIndex];
                                          const optOption = optNestedQuestion?.options[optOptionIndex];
                                          return t(optOption?.label || optKey);
                                        }) || [],
                                        notesForKeys: tagItem.notesForKeys || {}
                                      }));
                                      
                                      setTimeout(() => {
                                        onUpdate(combinedValue, answerMaps);
                                      }, 0);
                                      
                                      // surveyState에 allergyFinal 업데이트
                                      if (isNestedAllergyQuestion) {
                                        updateAllergyFinalState(newTags);
                                      }
                                    }}
                                    className="btn btn-ghost btn-xs btn-circle hover:bg-blue-200"
                                    style={{ 
                                      width: '16px', 
                                      height: '16px', 
                                      minWidth: '16px',
                                      minHeight: '16px'
                                    }}
                                  >
                                    <MdClose size={10} />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            ))}
          </div>
        )}
      </div>

      {/* 중첩 옵션 선택 모달 */}
      {openSelect && currentTag && (
        <Modal
          open
          className={classNames('t_QsInputMultiTextNested p-0 overflow-hidden rounded-t-3xl lg:max-w-[58rem] xl:max-w-[64rem]')}
          containerCls='pt-20 lg:pt-0'
        >
          <div className='flex-1 flex-col overflow-hidden'>
            <div className='flex-col flex-none gap-2 overflow-hidden pb-4 w-full'>
              <div className='flex-none flex-center'>
                <div className='bg-black bg-opacity-10 rounded-full w-10 h-2 mt-3'></div>
              </div>
              <div className='flex-none flex-center-y gap-2 px-5'>
                <h3 className='flex-1 text-center'>{currentTag.text} - {sltStr({ key: 'btn-add', upper: true })}</h3>
                <div className='w-0 overflow-visible'>
                  <button
                    className={classNames('p-2 inline')}
                    onClick={closeNestedOptions}
                  >
                    <MdClose size={20} className='opacity-60 -top-4 relative right-7' />
                  </button>
                </div>
              </div>
            </div>

            <div className='flex-col gap-3 overflow-auto px-3 pb-3'>
              {nestedQuestions.map((nestedQuestion, questionIndex) => (
                <div key={questionIndex} className='border border-gray-200 rounded-lg p-3'>
                  <div className='font-medium text-gray-700 mb-2'>
                    {getNestedQuestionLabel(nestedQuestion.question)}
                  </div>
                  <div className='space-y-2'>
                    {nestedQuestion.options.map((option, optionIndex) => {
                      const optionKey = `nested-${questionIndex}-${optionIndex}`;
                      const isSelected = currentTag.selectedOptions.includes(optionKey);
                      const requireNoteInput = isSelected && answerOptionsMap?.[optionKey]?.requireNote;
                      const extNoteId = `${optionKey}-ext-onsheet`;

                      return (
                        <div key={optionKey}>
                          <label className='cursor-pointer border border-gray-300 p-3 rounded-lg border-opacity-20 flex-col gap-3 hover:border-gray-400 transition-colors'>
                            <div className='flex-1 w-full flex-row gap-4'>
                              <input
                                value={optionKey}
                                checked={isSelected}
                                type="checkbox"
                                className="checkbox checkbox-lg checkbox-primary"
                                onChange={(e) => handleNestedOptionChange(optionKey, e.target.checked)}
                              />
                              <span className='pt-[0.2rem]'>{t(option.label)}</span>
                            </div>

                            {requireNoteInput && (
                              <label className='flex flex-col'>
                                <QsTextInput
                                  {...props}
                                  locale={locale}
                                  placeholder={sltStr({ key: 'label-describe-in-detail', upper: true })}
                                  value={currentTag.notesForKeys?.[optionKey] || ''}
                                  className={classNames(
                                    extNoteId,
                                    markError && isEmpty(trim(currentTag.notesForKeys?.[optionKey] || '')) && 'border-error',
                                    'input w-full border-1_5 border-black border-opacity-10 rounded-xl'
                                  )}
                                  onUpdate={(noteValue) => {
                                    const newTags = [...tags];
                                    const targetTag = newTags[selectedTagIndex!];
                                    if (!targetTag.notesForKeys) {
                                      targetTag.notesForKeys = {};
                                    }
                                    targetTag.notesForKeys[optionKey] = noteValue || '';
                                    setTags(newTags);
                                    
                                    // 부모 컴포넌트에 업데이트된 값 전달
                                    const combinedValue = newTags.map(tag => tag.text).join('|');
                                    const answerMaps = newTags.map(tag => ({
                                      answerKeys: tag.selectedOptions || [],
                                      answerLabels: tag.selectedOptions.map(optKey => {
                                        const optParts = optKey.split('-');
                                        const optQuestionIndex = parseInt(optParts[1], 10);
                                        const optOptionIndex = parseInt(optParts[2], 10);
                                        const optNestedQuestion = nestedQuestions[optQuestionIndex];
                                        const optOption = optNestedQuestion?.options[optOptionIndex];
                                        return t(optOption?.label || optKey);
                                      }) || [],
                                      notesForKeys: tag.notesForKeys || {}
                                    }));
                                    
                                    setTimeout(() => {
                                      onUpdate(combinedValue, answerMaps);
                                    }, 0);
                                    
                                    // surveyState에 allergyFinal 업데이트
                                    if (isNestedAllergyQuestion) {
                                      updateAllergyFinalState(newTags);
                                    }
                                  }}
                                />
                              </label>
                            )}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className='flex justify-end px-3 py-3'>
              <button
                className={classNames('btn')}
                onClick={closeNestedOptions}
              >
                {sltStr({ key: 'btn-close', capit: true })}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default memo(QsInputMultiTextNested);
